import { type ReactNode, useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from '@/hooks/use-media-query'

interface MerchantHoverCardProps {
  merchantBusinessName: string
  /** Stream slug — kept for API compat, no longer rendered in tooltip */
  slug?: string
  /** Short store description shown in the hover card */
  description?: string
  /** Year established — shown as "Since YYYY" */
  since?: number
  /** Avatar URL for the merchant/stream */
  avatarUrl?: string | null
  /**
   * Disable the hover card — children render as-is.
   * Use in any context where the card would be redundant.
   */
  disabled?: boolean
  children: ReactNode
}

/**
 * Wraps any trigger element with a hover card showing store meta-info.
 * Tooltip renders at position:fixed (calculated via getBoundingClientRect) so it
 * is never clipped by overflow:hidden ancestors — works inside OfferCard, drawers, etc.
 *
 * Shows: store name, description, "Since YYYY".
 * The tooltip is informational only — it is pointer-events-none; hover it or leave it,
 * the card dismisses only when the mouse leaves the trigger.
 *
 * Auto-disabled on stream detail pages (redundant when already inside the stream).
 * Disabled on touch screens (no hover state).
 */
export function MerchantHoverCard({ merchantBusinessName, slug, description, since, avatarUrl, disabled, children }: MerchantHoverCardProps) {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [imgError, setImgError] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Disable when viewing this specific stream's detail page (redundant info).
  // When slug is provided, compare against the URL slug — so the tote can show
  // tooltips for OTHER streams even while the user is on a stream detail page.
  const slugFromPath = pathname.match(/\/streams\/([^/]+)\/?$/)?.[1]
  const onStreamDetailPage = slug ? slugFromPath === slug : /\/streams\/[^/]+\/?$/.test(pathname)

  // Touch screens have no hover state — skip the card entirely
  const isTouch = useMediaQuery('(hover: none) and (pointer: coarse)')

  const show = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      if (!triggerRef.current) return
      const rect = triggerRef.current.getBoundingClientRect()
      setCoords({ top: rect.bottom + 4, left: rect.left })
      setVisible(true)
    }, 100)
  }, [])

  const hide = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setVisible(false), 80)
  }, [])

  if (disabled || onStreamDetailPage || isTouch) return <>{children}</>

  const tooltip = visible ? createPortal(
    <div
      role="tooltip"
      style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 9999 }}
      className="w-56 p-3 rounded-lg bg-white dark:bg-slate-800 border border-sand-200 dark:border-slate-700 shadow-md pointer-events-none"
    >
      {/* Header: avatar + name */}
      <div className="flex items-center gap-2.5 mb-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sand-200 dark:bg-slate-700 flex-shrink-0 overflow-hidden">
          {avatarUrl && !imgError ? (
            <img
              src={avatarUrl}
              alt=""
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <span className="text-[10px] font-semibold text-slate-600 dark:text-sand-300 select-none leading-none">
              {merchantBusinessName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          )}
        </div>
        <p className="text-sm font-semibold font-display text-slate-800 dark:text-sand-100 leading-tight">
          {merchantBusinessName}
        </p>
      </div>

      {/* Description */}
      {description && (
        <p className="text-xs text-slate-500 dark:text-slate-400 font-sans leading-relaxed line-clamp-2">
          {description}
        </p>
      )}

      {/* Since year */}
      {since && (
        <p className="text-xs text-slate-400 dark:text-slate-500 font-sans mt-1.5">
          {t('merchant.since', 'Since')} {since}
        </p>
      )}
    </div>,
    document.body
  ) : null

  return (
    <div ref={triggerRef} className="relative inline-block" onMouseEnter={show} onMouseLeave={hide}>
      {children}
      {tooltip}
    </div>
  )
}
